




import { useState, useMemo } from 'react'
import { useParams } from 'react-router-dom'

import { getNameOrEmailSlug } from 'utils'
import { datetime } from 'utils/date'

import { useDidMountEffect, usePagination } from 'hooks'
import { useCertificateProvenance } from 'hooks/api'
import { ProvenanceData, Company, Customer } from 'types'
import { ROUTES } from 'routes'
// Type-only — `@repo/types` isn't a runtime dependency of this app (unlike
// apps/app), so a value import (e.g. the enum itself) would fail to resolve
// at build time. String literals below carry the same 3 values.
import type { TransferCompletionMethod } from '@repo/types'

import { Stack, Box, Chip, Typography } from '@mui/material'
import { GridRenderCellParams } from '@mui/x-data-grid-pro';

import {
  Table,
  UserCell,
  CompanyBlock,
  StatusChip,
} from 'components'

// VERA-304 (Slice 3a) — evidence level a transfer completed with, reused as the
// "Verification" column so it reads the same as the app's TransferCompletionChip
// (scan_claimed = the "authentic/verified" tone, the other two = the neutral tone).
const COMPLETION_METHOD_STATUSES: Record<TransferCompletionMethod, { label: string; color: string }> = {
  transferred: { label: 'Transferred', color: '#6B7280' },
  claimed: { label: 'Claimed', color: '#6B7280' },
  scan_claimed: { label: 'Scan verified', color: '#16A34A' },
}

// How the owner acquired the certificate → plain-language label.
//   company origin → "Created" (the brand created/issued the certificate)
//   primary        → "Purchased" (the first owner bought it new)
//   sell           → "Purchased" (a resale)
//   gift           → "Gifted"
// (Only primary/sell/gift exist in TransferType today; richer methods —
//  Transferred / Inherited / Returned / etc. — would need new transfer types.)
// TODO(VRG-99): "Purchased via {marketplace}" (Chrono24 / eBay / P2P …) once the
// transfer carries a structured marketplace/channel field. Fall back to plain
// "Purchased" when unknown; primary (retail) has no marketplace.
const acquiredViaLabel = (via: string | null, isCompany: boolean): string => {
  if (isCompany) return 'Created'
  switch (via) {
    case 'primary': return 'Purchased'
    case 'sell': return 'Purchased'
    case 'gift': return 'Gifted'
    default: return '—'
  }
}


//

//

const DEFAULT_PAGINATION = {
  limit: 5,
  offset: 0,
}

export const Provenance = () => {
  const { id } = useParams()
  const [pagination, setPagination] = useState(DEFAULT_PAGINATION)

  const { data, refetch } = useCertificateProvenance({ certificateId: id as string, ...pagination })

  const { page } = usePagination({
    total: data?.total || 0,
    limit: pagination.limit,
    onChange: ({ limit, offset }) => setPagination({ limit, offset }),
  })

  useDidMountEffect(() => {
    refetch()
  }, [page])


  const isValidUrl = (value: string | null | undefined) => {
    if (!value || typeof value !== 'string') return false
    try {
      // eslint-disable-next-line no-new
      new URL(value)
      return true
    } catch {
      return false
    }
  }

  const columns = useMemo(
    () => [
      {
        field: 'owner_name',
        headerName: 'Owner',
        width: 320,
        pinned: 'left',
        type: 'string',
        // Shared UserCell (avatar-initials fallback + name/email). The current OWNER
        // gets a "Current" chip — not simply the top row, which may be a custody hop
        // (see `currentOwnerIndex`). Email is UserCell's secondary line, so the
        // standalone Email column is dropped as redundant.
        // pr makes the badge's right gap match the cell's 18px left padding (the cell's
        // own paddingRight is 11px, so add the 7px difference) — a Stack `spacing`
        // resets child margins, so the chip's own mr would be a no-op.
        renderCell: (params: GridRenderCellParams) => (
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2} sx={{ width: '100%', height: '100%', minWidth: 0, pr: '7px' }}>
            <Stack direction="row" alignItems="center" spacing={2} sx={{ minWidth: 0, flex: '0 1 auto' }}>
              {/* Ordinal position in the ownership chain (1 = original owner, growing
                  toward the current one) — helps at a glance without counting rows,
                  especially once the list is paginated. Muted so it doesn't compete
                  with the name/avatar. */}
              <Typography variant="body3" color="text.secondary" sx={{ flexShrink: 0, minWidth: 20 }}>
                #{params.row?.ordinal}
              </Typography>
              <Box sx={{ minWidth: 0, flex: '0 1 auto' }}>
                {params.row?.owner_type?.type === 'company' ? (
                  // The mint-origin row is the BRAND, not a person. Rendered through the
                  // shared CompanyBlock so it gets the rounded-square CompanyAvatar (logo
                  // → initials → store glyph) instead of UserCell's circular person
                  // avatar, which made a brand named "Louis Vuitton" read as a consumer of
                  // that name — the same confusion the Owner column's brand-held state
                  // exists to avoid. Unlinked: a company has no owner page.
                  <CompanyBlock
                    company={{
                      name: params.row?.owner_name,
                      logo: isValidUrl(params.row?.avatar) ? params.row?.avatar : undefined,
                    }}
                  />
                ) : (
                  <UserCell
                    user={{
                      name: params.row?.owner_name,
                      email: params.row?.email,
                      avatar: isValidUrl(params.row?.avatar) ? params.row?.avatar : undefined,
                    }}
                    route={
                      params.row?.owner_type?.customer_id
                        ? ROUTES.OWNER(params.row.owner_type.customer_id)
                        : undefined
                    }
                  />
                )}
              </Box>
            </Stack>
            {params.row?.is_last_owner && (
              // A badge, not a status chip: square corners, no border, a slight grey
              // fill (grey[100], the app's subtle-bg token — parity with PreviewList).
              <Chip
                label="Current"
                size="small"
                sx={{
                  height: 22,
                  fontSize: 11,
                  flexShrink: 0,
                  borderRadius: '6px',
                  border: 'none',
                  backgroundColor: (t) => t.palette.grey[100],
                  color: (t) => t.palette.text.secondary,
                  '& .MuiChip-label': { px: 1.5 },
                }}
              />
            )}
          </Stack>
        )
      },
      {
        // VERA-304 verify-walkthrough feedback: "Claimed date" was factually wrong
        // for `transferred`/`scan_claimed` rows — only one of the three completion
        // methods is actually a "claim". Renamed to "Ownership date" and moved to
        // the 2nd column (right after Owner).
        field: 'ownership_date',
        headerName: 'Ownership date',
        type: 'dateTime',
        width: 220,
        // One date format everywhere (working-conventions): ordinal day, 12-hour
        // time — "Jun 5th 2026, 6:34 AM". The shared Table's default dateTime
        // rendering falls back to the browser locale, so this is set explicitly.
        valueFormatter: (value: any) => (value ? datetime(value, 'MMM do yyyy, h:mm a') : '-'),
      },
      {
        field: 'acquired_via',
        headerName: 'Acquired via',
        type: 'string',
        width: 160,
        // Extra left gap so it isn't tight against the pinned Owner column's border.
        renderHeader: () => <Box sx={{ pl: 2 }}>Acquired via</Box>,
        renderCell: (params: GridRenderCellParams) => <Box sx={{ pl: 2 }}>{params.value}</Box>,
      },
      {
        // VERA-304 verify-walkthrough feedback: parity with the app's
        // TransferCompletionChip on OwnerProvenanceCard, which the platform table
        // had no equivalent of. Null (the company/mint-origin row, or a legacy
        // transfer predating this field) renders nothing.
        field: 'completion_method',
        headerName: 'Verification',
        type: 'string',
        width: 160,
        sortable: false,
        renderCell: (params: GridRenderCellParams) =>
          params.value ? <StatusChip statuses={COMPLETION_METHOD_STATUSES} status={params.value} size="small" /> : null,
      },
      {
        // `currency` type → shared Table formats the raw number with commas
        // (5200 → "$5,200.00") and renders null as "-".
        field: 'price',
        headerName: 'Price',
        type: 'currency',
        width: 150,
      },
    ], []
  )


  // const provenance = parseData(data?.data || [], data?.offset || 0)

  const provenance = useMemo(() => {
    const list = Array.isArray(data?.data) ? data?.data : []
    const total = data?.total ?? list.length
    /*
      VERA-931 — which row is the CURRENT OWNER.

      It used to be `index === 0`: whichever row happened to be on top. Once custody
      hops joined the chain that became wrong in a way that matters — a certificate
      whose most recent link is a shop rendered "Current" against the SHOP, so the
      page said a boutique owns a bag the brand still owns. A hop is custody, not
      ownership; this file's own store branch says as much, and the public builder
      already forces `is_current_owner: false` for exactly this reason.

      So the badge goes to the most recent row that is actually an OWNERSHIP link,
      skipping stores. On a chain that ends at a shop that is the brand's origin row,
      which is the truthful answer: the brand never stopped owning it.

      Only ever on the first page: `index` is page-relative, so on page 2 the top row
      is not the current owner at all.
    */
    const currentOwnerIndex =
      pagination.offset === 0
        ? list.findIndex((row: ProvenanceData) => row.owner_type !== 'store')
        : -1
    return list.map((item: ProvenanceData, index: number) => {
      // Rows are current-first (index 0 = most recent). Ordinal counts up from the
      // ORIGINAL owner (#1) to the current one (#total), across pagination.
      const ordinal = total - (pagination.offset + index)
      /*
        VERA-931 — a STORE row. Handled before the customer fallback because that
        fallback reads `name`, `email` and `avatar`, none of which a store has: the
        row rendered with a completely EMPTY owner cell, which reads as missing data
        rather than as a place the item passed through.

        A store is a business, so it takes the same CompanyBlock treatment as the
        brand's own origin row — rounded-square avatar, no person chrome, no link
        (a store has no owner page). The city is appended because "Bond Street"
        alone is ambiguous across a chain that may include several.
      */
      if (item.owner_type === 'store') {
        const store = item.owner as {id?: string; label?: string | null; city?: string | null}
        const label = store?.label ?? 'Store'
        return {
          id: `${item.certificate.id}-${index}`,
          owner_name: store?.city ? `${label} · ${store.city}` : label,
          email: null,
          phone: null,
          avatar: null,
          ownership_date: item.ownership_date ?? null,
          /*
            NOT `acquiredViaLabel(...)`. That helper answers "how did this owner
            acquire it", and with a null transfer type and the company flag it
            returns "Created" — which on a store row asserts the item was minted
            there. It was not: the brand created it and later sent it. "Received"
            is the only true verb for this link.
          */
          acquired_via: 'Received',
          completion_method: item.completion_method ?? null,
          price: item.price ?? null,
          owner_type: { type: 'company', company_id: store?.id },
          // Never. A shop held the item; it did not own it.
          is_last_owner: false,
          ordinal,
        }
      }

      const isCompany = item.owner_type === 'company'
      if (isCompany) {
        const company = item.owner as Company
        const name = company?.name
        return {
          id: `${item.certificate.id}-${index}`,
          owner_name: name ?? '',
          email: null,
          phone: null,
          avatar: isValidUrl(company?.logo) ? company?.logo : null,
          ownership_date: item.ownership_date ?? null,
          acquired_via: acquiredViaLabel(item.acquired_via, true),
          completion_method: item.completion_method ?? null,
          price: item.price ?? null,
          owner_type: { type: 'company', company_id: company?.id },
          is_last_owner: index === currentOwnerIndex,
          ordinal,
        }
      }
      const customer = item.owner as Customer
      const name = getNameOrEmailSlug(customer)
      return {
        id: `${item.certificate.id}-${index}`,
        owner_name: name ?? '',
        email: customer?.email ?? null,
        phone: customer?.phone ?? null,
        avatar: customer?.avatar ?? null,
        ownership_date: item.ownership_date ?? null,
        acquired_via: acquiredViaLabel(item.acquired_via, false),
        completion_method: item.completion_method ?? null,
        price: item.price ?? null,
        owner_type: { type: 'customer', customer_id: customer?.id },
        is_last_owner: index === currentOwnerIndex,
        ordinal,
      }
    })
  }, [data, pagination.offset])

  return (
    <Table
      fillBottom
      columns={columns}
      rows={provenance}
    // NO EMPTY STATE becuase there will alwasy be an owner
    // emptyState={
    //   <EmptyState
    //     title="No owners yet"
    //     text="Use the button below to transfer certificate"
    //     icon={<UserPlusIcon strokeWidth={1} size={32} />}
    //     buttons={
    //       <Button
    //       variant="contained"
    //       disableElevation
    //       sx={{ background: theme.palette.primary[950], textTransform: 'capitalize', borderRadius: 2, height: '40px' }}
    //       // onClick={() => handleCreateOwner()}
    //       startIcon={<UserPlusIcon strokeWidth={1} />}
    //       >
    //         Transfer Certificate
    //       </Button>
    //     }/>    
    // }
    />

  )
}
